/**
 * Formats a KPI metric value for display.
 *
 * Numeric values — including floats and numeric strings — are run through Intl
 * number formatting (locale grouping + decimal separators) via
 * `numberToIntlString`, so `142` → "142", `1234.56` → "1,234.56", and a
 * backend-serialized "1234.56" formats the same. Currency strings carrying a
 * leading symbol (e.g. "$393689.12") have the number grouped and the symbol
 * re-attached, so "$393689.12" → "$393,689.12". Other non-numeric strings are
 * passed through untouched — the backend may have already formatted them (e.g.
 * "12.5%" or "N/A"). Null/undefined collapses to an empty string; callers
 * supply their own "N/A" fallback upstream.
 */
import {numberToIntlString} from 'src/utils/number';

// A plain integer or float, e.g. "142", "-3.5", ".75" — but NOT "1,000"
// (already grouped), "12.5%", "$10", or "1e3" (those are left as-is).
const NUMERIC_STRING = /^-?(?:\d+\.?\d*|\.\d+)$/;

// A leading currency symbol (with optional sign on either side) followed by a
// plain number, e.g. "$393689.12", "-$100.00", "€1234.5". `\p{Sc}` matches any
// Unicode currency symbol ($, €, £, ¥, ₹, …). The number is grouped and the
// original prefix re-attached. Suffix-formatted strings like "12.5%" don't
// match and fall through untouched.
const CURRENCY_STRING = /^(-?\p{Sc}-?)(\d+\.?\d*|\.\d+)$/u;

export function formatKpiValue(
  value: number | string | null | undefined,
): string {
  if (value == null) {
    return '';
  }
  if (typeof value === 'number') {
    return numberToIntlString(value);
  }
  const trimmed = value.trim();
  if (NUMERIC_STRING.test(trimmed)) {
    return numberToIntlString(Number(trimmed));
  }
  const currencyMatch = CURRENCY_STRING.exec(trimmed);
  if (currencyMatch) {
    const [, prefix, num] = currencyMatch;
    return `${prefix}${numberToIntlString(Number(num))}`;
  }
  return value;
}
